// Small UI helpers: safe HTML templates, icons, toasts and the app shell.
import { currentSession, signOut } from './supa';

/** Escapes text for HTML. */
export const esc = (v: unknown) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

/** Raw HTML marker, so nested templates are not escaped twice. */
export class Raw { constructor(public html: string) {} }
export const raw = (html: string) => new Raw(html);

/** Tagged template: interpolated values are escaped unless wrapped in raw() or produced by html``. */
export function html(strings: TemplateStringsArray, ...values: unknown[]): Raw {
  let out = '';
  strings.forEach((s, i) => {
    out += s;
    if (i < values.length) {
      const v = values[i];
      if (v instanceof Raw) out += v.html;
      else if (Array.isArray(v)) out += v.map((x) => (x instanceof Raw ? x.html : esc(x))).join('');
      else if (v === false || v == null) out += '';
      else out += esc(v);
    }
  });
  return new Raw(out);
}

export const $ = <T extends HTMLElement = HTMLElement>(sel: string, root: ParentNode = document) => root.querySelector(sel) as T;
export const $$ = <T extends HTMLElement = HTMLElement>(sel: string, root: ParentNode = document) => Array.from(root.querySelectorAll(sel)) as T[];

export const icons = {
  grid: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><rect x="3.5" y="3.5" width="7" height="7" rx="1.5"/><rect x="13.5" y="3.5" width="7" height="7" rx="1.5"/><rect x="3.5" y="13.5" width="7" height="7" rx="1.5"/><rect x="13.5" y="13.5" width="7" height="7" rx="1.5"/></svg>',
  chart: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><path d="M4 20V10"/><path d="M10 20V4"/><path d="M16 20v-7"/><path d="M22 20H2"/></svg>',
  palette: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><path d="M12 3a9 9 0 1 0 0 18c1.2 0 1.8-.9 1.8-1.8 0-1.4-1.2-1.6-1.2-2.8 0-1 .8-1.6 1.8-1.6H17a4 4 0 0 0 4-4C21 6.4 17 3 12 3z"/></svg>',
  mail: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3.5 7l8.5 6 8.5-6"/></svg>',
  team: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"><circle cx="9" cy="8" r="3.5"/><path d="M2.5 20c.8-3.5 3.4-5.5 6.5-5.5s5.7 2 6.5 5.5"/><path d="M16 4.8a3.5 3.5 0 0 1 0 6.4"/><path d="M18.5 14.8c1.7.8 2.7 2.6 3 5.2"/></svg>',
  cog: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>',
  card: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><rect x="2.5" y="5" width="19" height="14" rx="2.5"/><path d="M2.5 10h19"/></svg>',
  close: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>',
  download: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 4v11"/><path d="M7.5 10.5 12 15l4.5-4.5"/><path d="M5 19h14"/></svg>',
  guide: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M10 8.5l5.5 3.5-5.5 3.5z" fill="currentColor"/></svg>',
  up: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 15l6-6 6 6"/></svg>',
  down: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>',
  more: '<svg viewBox="0 0 24 24" fill="currentColor"><circle cx="5.5" cy="12" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="18.5" cy="12" r="1.8"/></svg>',
  upload: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 15V4"/><path d="M7.5 8.5 12 4l4.5 4.5"/><path d="M5 19h14"/></svg>',
  trash: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M4 7h16"/><path d="M9 7V4.5h6V7"/><path d="M6.5 7l1 13h9l1-13"/></svg>',
  image: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><rect x="3.5" y="5" width="17" height="14" rx="2.5"/><circle cx="9" cy="10" r="1.8"/><path d="M4 17l5-4.5 4 3.5 3-2.5 4 3.5"/></svg>',
  spark: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><path d="M12 3l1.8 5.2L19 10l-5.2 1.8L12 17l-1.8-5.2L5 10l5.2-1.8z"/><path d="M18.5 15.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7z"/></svg>',
  copy: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linejoin="round"><rect x="8.5" y="8.5" width="11" height="11" rx="2.5"/><path d="M15.5 8.5V6A1.5 1.5 0 0 0 14 4.5H6A1.5 1.5 0 0 0 4.5 6v8A1.5 1.5 0 0 0 6 15.5h2.5"/></svg>',
  search: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="6.5"/><path d="M20 20l-4-4"/></svg>',
  plus: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>',
  back: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M15 5l-7 7 7 7"/></svg>',
  tick: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>',
  spin: '<svg class="spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round"><path d="M12 3a9 9 0 1 1-9 9"/></svg>',
  folder: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><path d="M3 6.5A1.5 1.5 0 0 1 4.5 5h4.6l2 2.2h8.4A1.5 1.5 0 0 1 21 8.7v9.8a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18.5z"/></svg>',
  monitor: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4.5" width="18" height="12" rx="2"/><path d="M9 20h6M12 16.5V20"/></svg>',
  phone: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="7" y="3" width="10" height="18" rx="2.5"/><path d="M11 18h2"/></svg>',
  video: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"><rect x="3" y="6" width="13" height="12" rx="2.5"/><path d="M16 10.5 21 8v8l-5-2.5z"/></svg>',
  eye: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z"/><circle cx="12" cy="12" r="3"/></svg>',
  help: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M9.6 9.3a2.5 2.5 0 0 1 4.85.85c0 1.65-2.45 2.2-2.45 3.6"/><path d="M12 17h.01"/></svg>',
  out: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M14 4h4.5A1.5 1.5 0 0 1 20 5.5v13a1.5 1.5 0 0 1-1.5 1.5H14"/><path d="M10 16l-4-4 4-4"/><path d="M6 12h10"/></svg>',
};
export const icon = (name: keyof typeof icons, size = 18) => raw(`<span class="ic" style="width:${size}px;height:${size}px">${icons[name]}</span>`);

let toastTimer: number | undefined;
export function toast(msg: string, kind: 'info' | 'error' = 'info', ms = 4000): void {
  let t = $('#toast');
  if (!t) { t = document.createElement('div'); t.id = 'toast'; document.body.appendChild(t); }
  t.className = `toast ${kind}`;
  t.textContent = msg;
  t.hidden = false;
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => { t.hidden = true; }, ms);
}

const KB = 1024, MB = KB * 1024, GB = MB * 1024;
export const formatBytes = (b: number) =>
  b >= GB ? `${(b / GB).toFixed(2)} GB` : b >= MB ? `${Math.round(b / MB)} MB` : b >= KB ? `${Math.round(b / KB)} KB` : `${b} B`;
/** A project's storage limit as a round label: 500 MB, 1 GB, 2.5 GB. */
export const formatLimit = (b: number) => (b >= GB ? `${+(b / GB).toFixed(1)} GB` : `${Math.round(b / MB)} MB`);

export type NavKey = 'projects' | 'admin' | 'overview' | 'setup' | 'analytics' | 'brand' | 'enquiries' | 'billing';
export type SetupSection = 'start' | 'rooms' | 'hotspots' | 'alts' | 'levels' | 'options';
/** Sections of the Analytics and Brand kit pages, nested in the sidebar; links jump down the page. */
export const ANALYTICS_SECTIONS = [
  { id: 'visits', label: 'Visits' }, { id: 'heatmap', label: 'Heatmap' }, { id: 'rooms', label: 'Rooms' }, { id: 'finishes', label: 'Finishes' },
  { id: 'places', label: 'Places' },
];
export const BRAND_SECTIONS = [{ id: 'look', label: 'Look' }, { id: 'loading', label: 'Loading screen' }, { id: 'extras', label: 'Extras' }];
/** Brand kit sections before they were grouped: old links still land in the right place. */
export const BRAND_OLD: Record<string, string> = { styles: 'look', logo: 'look', theme: 'look', music: 'extras', kiosk: 'extras' };
export const SETUP_SECTIONS: Array<{ id: SetupSection; label: string }> = [
  { id: 'rooms', label: 'Rooms and levels' }, { id: 'start', label: 'Start view' }, { id: 'hotspots', label: 'Hotspots' },
  { id: 'alts', label: 'Finishes' }, { id: 'options', label: 'Options' },
];
export interface ShellState {
  studioName: string;
  active: NavKey;
  /** Inside Set up tour: which section is open (shown nested under it); on Analytics or Brand kit, where to jump. */
  sub?: string;
  /** Inside a project the sidebar switches to that project's pages. */
  project?: { id: string; name?: string };
}

let adminCog = false;
/** Shows the admin settings cog in the sidebar (set once after sign-in). */
export const setAdminCog = (on: boolean) => { adminCog = on; };

/**
 * Sidebar layout. On the projects list it holds only Projects; inside a project it lists that
 * project's pages (overview, set up, analytics, brand kit, team, billing). Returns the page element.
 */
export function shell(root: HTMLElement, s: ShellState): HTMLElement {
  const email = currentSession()?.user.email ?? '';
  const item = (key: NavKey, label: string, ic: keyof typeof icons, href: string | null) =>
    href
      ? html`<a class="nav ${s.active === key ? 'on' : ''}" href="${href}">${icon(ic)}${label}</a>`
      : html`<span class="nav soon" title="Coming soon">${icon(ic)}${label}<em>Soon</em></span>`;
  const p = s.project;
  const nav = p
    ? html`<a class="side-back" href="#/">${icon('back', 14)}All projects</a>
        <div class="side-project" id="side-project" title="${p.name ?? ''}">${p.name ?? ''}</div>
        ${item('overview', 'Overview', 'grid', `#/p/${p.id}`)}
        ${item('setup', 'Set up tour', 'eye', `#/p/${p.id}/setup`)}
        ${s.active === 'setup' ? html`<div class="nav-sub">${SETUP_SECTIONS.map((x) => html`<a class="${s.sub === x.id ? 'on' : ''}" href="#/p/${p.id}/setup/${x.id}" data-sec="${x.id}">${x.label}</a>`)}</div>` : ''}
        ${item('brand', 'Brand kit', 'palette', `#/p/${p.id}/brand`)}
        ${s.active === 'brand' ? html`<div class="nav-sub">${BRAND_SECTIONS.map((x) => html`<a class="${s.sub === x.id ? 'on' : ''}" href="#/p/${p.id}/brand/${x.id}" data-jump="${x.id}">${x.label}</a>`)}</div>` : ''}
        <a class="nav ${s.active === 'enquiries' ? 'on' : ''}" href="#/p/${p.id}/enquiries">${icon('mail')}Enquiries<span class="nav-badge" id="side-unread" hidden></span></a>
        ${item('analytics', 'Analytics', 'chart', `#/p/${p.id}/analytics`)}
        ${s.active === 'analytics' ? html`<div class="nav-sub">${ANALYTICS_SECTIONS.map((x) => html`<a class="${s.sub === x.id ? 'on' : ''}" href="#/p/${p.id}/analytics/${x.id}" data-jump="${x.id}">${x.label}</a>`)}</div>` : ''}
        <button class="nav" id="side-billing" hidden>${icon('card')}Billing</button>`
    : html`${item('projects', 'Projects', 'grid', '#/')}`;
  root.innerHTML = html`<div class="shell">
    <aside class="side">
      <a href="#/" class="side-logo"><img src="/archviewvr-logo.svg" alt="ArchviewVR"></a>
      ${nav}
      <a class="nav side-help" href="https://archviewvr.com/help" target="_blank" rel="noopener">${icon('help')}Need help?</a>
      <div class="side-foot">
        <div class="studio-name">${s.studioName}</div>
        <div class="studio-email">${email}</div>
        <div class="foot-row">
          <button class="link-btn" id="sign-out">${icon('out', 15)}Sign out</button>
          ${adminCog ? html`<a class="cog ${s.active === 'admin' ? 'on' : ''}" href="#/admin" title="Admin" aria-label="Admin">${icon('cog', 17)}</a>` : ''}
        </div>
      </div>
    </aside>
    <main class="main" id="page"></main>
  </div>`.html;
  $('#sign-out', root).onclick = () => { void signOut(); };
  return $('#page', root);
}

/** Top bar used by the project flow (back button, title, step pills, actions). */
/** The five steps of making a tour. With a project id, finished steps link back (Check only while an upload is waiting). */
export function stepPills(step: number, projectId?: string, _canCheck = false): Raw {
  const steps = ['Details', 'Upload', 'Check', 'Set up', 'Publish'];
  const href = (n: number) => !projectId ? null
    : n === 1 || n === 5 ? `#/p/${projectId}` : n === 2 ? `#/p/${projectId}/upload` : n === 3 ? `#/p/${projectId}/check` : `#/p/${projectId}/setup`;
  const pills = steps.map((label, i) => {
    const n = i + 1;
    const cls = n < step ? 'step done' : n === step ? 'step on' : 'step';
    const mark = n < step ? icons.tick : String(n);
    const to = n === step ? null : href(n);
    return to
      ? `<li class="${cls}"><a href="${to}"><span>${mark}</span>${label}</a></li>`
      : `<li class="${cls}"><span>${mark}</span>${label}</li>`;
  }).join('');
  return raw(`<ol class="steps">${pills}</ol>`);
}

export function flowHeader(opts: { back: string; eyebrow: string; title: string; step?: number; actions?: Raw; project?: string; canCheck?: boolean }): Raw {
  return html`<header class="flow-top">
    <div class="flow-title">
      <a class="round-btn" href="${opts.back}" aria-label="Back">${icon('back', 16)}</a>
      <div><div class="eyebrow">${opts.eyebrow}</div><div class="title">${opts.title}</div></div>
    </div>
    ${opts.step == null ? '' : stepPills(opts.step, opts.project, opts.canCheck)}
    <div class="flow-actions">${opts.actions ?? ''}</div>
  </header>`;
}

/** A small confirm dialog. With typeToConfirm, the button stays off until that text is typed. */
export function confirmDialog(o: { title: string; body: Raw | string; confirm: string; danger?: boolean; typeToConfirm?: string }): Promise<boolean> {
  return new Promise((resolve) => {
    const d = document.createElement('dialog');
    d.className = 'dlg';
    d.innerHTML = html`<form method="dialog">
      <h2>${o.title}</h2>
      <div class="dlg-body">${o.body}</div>
      ${o.typeToConfirm ? html`<label class="dlg-type"><span>Type <b>${o.typeToConfirm}</b> to confirm</span><input autocomplete="off" spellcheck="false"></label>` : ''}
      <div class="btn-row"><button class="btn" value="no">Cancel</button><button class="btn ${o.danger ? 'danger' : 'primary'}" value="yes" ${o.typeToConfirm ? raw('disabled') : ''}>${o.confirm}</button></div>
    </form>`.html;
    document.body.append(d);
    const yes = d.querySelector('button[value=yes]') as HTMLButtonElement;
    const input = d.querySelector('input');
    if (input) input.oninput = () => { yes.disabled = input.value.trim().toLowerCase() !== o.typeToConfirm!.toLowerCase(); };
    d.addEventListener('close', () => { resolve(d.returnValue === 'yes'); d.remove(); });
    d.showModal();
    (input ?? yes).focus();
  });
}

/** A dialog with a few choices stacked as cards. Resolves with the chosen value, or null when cancelled. */
export function choiceDialog<T extends string>(o: { title: string; body: Raw | string; choices: Array<{ value: T; label: string; note: string; primary?: boolean }> }): Promise<T | null> {
  return new Promise((resolve) => {
    const d = document.createElement('dialog');
    d.className = 'dlg';
    d.innerHTML = html`<form method="dialog">
      <h2>${o.title}</h2>
      <div class="dlg-body">${o.body}</div>
      <div class="dlg-choices">${o.choices.map((c) => html`<button class="dlg-choice${c.primary ? ' primary' : ''}" value="${c.value}"><b>${c.label}</b><span>${c.note}</span></button>`)}</div>
      <div class="btn-row"><button class="btn" value="">Cancel</button></div>
    </form>`.html;
    document.body.append(d);
    d.addEventListener('close', () => { resolve((d.returnValue || null) as T | null); d.remove(); });
    d.showModal();
    d.querySelector<HTMLButtonElement>('.dlg-choice')?.focus();
  });
}

/**
 * Fills in the project sidebar once the project has loaded: its name, how many enquiries are new, and
 * Billing, which only shows once the project has a subscription.
 */
export function setShellProject(root: HTMLElement, p: { name: string; state: string; stripe_subscription_id?: string | null }, openBilling: () => Promise<void>, unread?: Promise<number>): void {
  const name = root.querySelector<HTMLElement>('#side-project');
  if (name) { name.textContent = p.name; name.title = p.name; }
  void unread?.then((n) => setUnread(root, n)).catch(() => undefined);
  const bill = root.querySelector<HTMLButtonElement>('#side-billing');
  if (!bill) return;
  const can = p.state === 'published' || !!p.stripe_subscription_id;
  bill.hidden = !can;
  bill.title = 'Cards, invoices and cancelling, on Stripe';
  bill.onclick = async () => {
    bill.disabled = true;
    try { await openBilling(); } catch (e) { toast(e instanceof Error ? e.message : 'Couldn’t open billing', 'error', 8000); bill.disabled = false; }
  };
}

/** The sidebar's count of new enquiries. */
export function setUnread(root: ParentNode, n: number): void {
  const b = root.querySelector<HTMLElement>('#side-unread');
  if (!b) return;
  b.hidden = n <= 0; b.textContent = n > 99 ? '99+' : String(n);
  b.setAttribute('aria-label', `${n} new`);
}

/**
 * Sidebar links that jump down a long page (Analytics, Brand kit): scrolls to the matching
 * data-anchor, keeps the address in step, and highlights the section on screen while scrolling.
 */
export function wireJumps(root: HTMLElement, page: HTMLElement, start?: string): void {
  const links = [...root.querySelectorAll<HTMLAnchorElement>('.nav-sub [data-jump]')];
  if (!links.length) return;
  let hold = 0;
  const mark = (id: string) => links.forEach((a) => a.classList.toggle('on', a.dataset.jump === id));
  const go = (id: string) => {
    const el = page.querySelector<HTMLElement>(`[data-anchor="${id}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'start' }); // instant: smooth scrolling gets cut short while a tour preview is drawing
    mark(id);
    hold = performance.now() + 1200; // the clicked section stays marked while the page scrolls (or can't scroll further)
  };
  links.forEach((a) => (a.onclick = (e) => {
    e.preventDefault();
    history.replaceState(null, '', a.getAttribute('href'));
    go(a.dataset.jump!);
  }));
  const prev = (page as HTMLElement & { _spy?: IntersectionObserver })._spy;
  prev?.disconnect();
  const spy = new IntersectionObserver((entries) => {
    const vis = entries.filter((x) => x.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
    if (vis && performance.now() > hold) mark((vis.target as HTMLElement).dataset.anchor!);
  }, { rootMargin: '0px 0px -70% 0px' });
  page.querySelectorAll('[data-anchor]').forEach((x) => spy.observe(x));
  (page as HTMLElement & { _spy?: IntersectionObserver })._spy = spy;
  if (start) requestAnimationFrame(() => go(start));
}

// ---------------------------------------------------------------- tour preview: desktop or phone
export type PreviewDevice = 'desktop' | 'phone';
/** The Desktop / Phone switch shown above a tour preview. */
export const deviceSwitch = () => html`<div class="seg small" aria-label="Device"><button data-device="desktop">${icon('monitor', 15)}Desktop</button><button data-device="phone">${icon('phone', 15)}Phone</button></div>`;
/** The preview stage: an iframe in a box sized like a computer screen or a phone. */
export const previewStage = (id: string, src: string, attrs = '') =>
  html`<div class="pv-stage"><div class="pv-device"><iframe id="${id}" src="${src}" title="Tour preview" allow="fullscreen" ${raw(attrs)}></iframe></div></div>`;
/**
 * Sizes a preview stage for the chosen device (tall enough to really see the tour: 80% of the window on computers,
 * 90% on phones), wires its Desktop / Phone buttons and keeps it fitted as the page resizes. Returns a refit function.
 */
export function wirePreview(scope: HTMLElement, opts: { onChange?: (d: PreviewDevice) => void; get?: () => PreviewDevice; set?: (d: PreviewDevice) => void } = {}): () => void {
  let local: PreviewDevice = 'desktop';
  const get = opts.get ?? (() => local), set = opts.set ?? ((d: PreviewDevice) => { local = d; });
  const stage = $('.pv-stage', scope), dev = $('.pv-device', scope);
  const fit = () => {
    const device = get();
    const small = innerWidth <= 760;
    const tall = innerHeight * (small ? 0.9 : 0.8);
    const sw = stage.clientWidth;
    let w: number, h: number, scale: number;
    if (device === 'phone') { [w, h] = [390, 780]; scale = Math.min(1, sw / w, tall / h); }
    else if (small) { w = sw; h = tall; scale = 1; } // on a phone, the desktop view is just the full-size tour
    else { scale = Math.min(1, sw / 1280); w = sw / scale; h = tall / scale; }
    dev.style.width = `${w}px`; dev.style.height = `${h}px`; dev.style.transform = `scale(${scale})`;
    dev.style.left = `${Math.max(0, (sw - w * scale) / 2)}px`;
    stage.style.height = `${h * scale}px`;
    stage.classList.toggle('phone', device === 'phone');
    $$<HTMLButtonElement>('[data-device]', scope).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.device === device)));
  };
  new ResizeObserver(fit).observe(stage);
  $$<HTMLButtonElement>('[data-device]', scope).forEach((b) => (b.onclick = () => { set(b.dataset.device as PreviewDevice); fit(); opts.onChange?.(get()); }));
  fit();
  return fit;
}
